import { useEffect, useState, type FormEvent } from 'react'
import Field from '../components/forms/Field'
import { useNavigate } from 'react-router-dom'
import { apiFetch, apiJson } from '../lib/api'
import PhotoUpload from '../components/PhotoUpload'

interface MeResponse {
  profile: {
    id: string
    email: string
    display_name: string | null
    role: string | null
  }
}

interface FormState {
  name: string
  current_team: string
  country: string
  role: string
  fiba_cert_number: string
  bio: string
}

const initial: FormState = {
  name: '',
  current_team: '',
  country: '',
  role: '',
  fiba_cert_number: '',
  bio: '',
}

export default function OnboardingCoach() {
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState>(initial)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Photo upload at setup runs in deferred mode — the coaches row doesn't
  // exist yet, so /api/upload/photo (requireCoachOwner) would 403 if we
  // tried to upload on file pick. We hold the resized blob locally and
  // POST it after the coach is created on submit. Photo is optional;
  // submit is happy with `photoBlob === null`.
  const [photoBlob, setPhotoBlob] = useState<Blob | null>(null)

  useEffect(() => {
    let cancelled = false
    async function bootstrap() {
      try {
        const me = await apiJson<MeResponse>('/api/me')
        if (cancelled) return
        // Permit coach (already past /onboarding) and null (direct visit
        // hits POST /api/coach which will bootstrap role atomically).
        if (me.profile.role !== null && me.profile.role !== 'coach') {
          navigate('/onboarding', { replace: true })
          return
        }
        setForm(f => ({ ...f, name: me.profile.display_name ?? '' }))
        setLoading(false)
      } catch {
        if (cancelled) return
        navigate('/', { replace: true })
      }
    }
    bootstrap()
    return () => {
      cancelled = true
    }
  }, [navigate])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setErr(null)
    try {
      await apiJson('/api/coach', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          current_team: form.current_team.trim() || null,
          country: form.country.trim() || null,
          role: form.role.trim() || null,
          fiba_cert_number: form.fiba_cert_number.trim() || null,
          bio: form.bio.trim() || null,
        }),
      })
      // The coaches row now exists and the user's role is 'coach', so
      // /api/upload/photo (requireCoachOwner) will resolve. Photo upload
      // is best-effort: if it fails, the profile is still good and the
      // user can retry from /coach/edit. Don't strand them on setup.
      if (photoBlob) {
        try {
          const fd = new FormData()
          fd.append(
            'file',
            new File([photoBlob], 'photo.jpg', { type: 'image/jpeg' }),
          )
          fd.append('entity_type', 'coach')
          const res = await apiFetch('/api/upload/photo', {
            method: 'POST',
            body: fd,
          })
          if (!res.ok) {
            console.warn(
              '[onboarding-coach] photo upload failed post-create',
              res.status,
            )
          }
        } catch (uploadErr) {
          console.warn(
            '[onboarding-coach] photo upload threw post-create',
            uploadErr,
          )
        }
      }
      navigate('/dashboard', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-hoop-bg">
        <p className="text-hoop-text-3">Loading…</p>
      </main>
    )
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-hoop-bg">
      <form onSubmit={submit} className="max-w-xl w-full space-y-5">
        <header>
          <h1 className="text-3xl font-bold text-hoop-text">
            Set up your coach profile
          </h1>
          <p className="text-hoop-text-2 mt-1 text-sm">
            You'll use this profile to write coach references for players you've worked with.
          </p>
        </header>

        <PhotoUpload
          entityType="coach"
          currentPhotoUrl={null}
          onChange={() => {}}
          deferred
          onFilePicked={setPhotoBlob}
          label="Profile photo (optional)"
        />

        <Field label="Your name" required>
          <input
            type="text"
            required
            value={form.name}
            onChange={e => update('name', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Current team / program" hint="e.g. 'Real Madrid B' or 'TMU Bold'">
          <input
            type="text"
            value={form.current_team}
            onChange={e => update('current_team', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Country">
          <input
            type="text"
            value={form.country}
            onChange={e => update('country', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Role" hint="e.g. 'Head Coach', 'Assistant', 'Scout'">
          <input
            type="text"
            value={form.role}
            onChange={e => update('role', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="FIBA certification number" hint="Optional. Verified profiles get a badge.">
          <input
            type="text"
            value={form.fiba_cert_number}
            onChange={e => update('fiba_cert_number', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Bio">
          <textarea
            rows={4}
            value={form.bio}
            onChange={e => update('bio', e.target.value)}
            className={inputClass}
          />
        </Field>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="bg-hoop-primary text-hoop-primary-fg px-6 py-3 rounded-lg font-medium hover:opacity-90 disabled:opacity-40"
          >
            {submitting ? 'Saving…' : 'Create coach profile'}
          </button>
          {err && (
            <p className="text-sm text-hoop-red">{err}</p>
          )}
        </div>
      </form>
    </main>
  )
}

const inputClass =
  'w-full rounded-md border border-hoop-border bg-hoop-surface px-3 py-2 text-sm text-hoop-text placeholder:text-hoop-text-3 focus:outline-none focus:ring-2 focus:ring-hoop-accent'

